import React, { useState } from 'react';
import axios from 'axios';
import { FiLock } from 'react-icons/fi';
import { useLanguage } from '../components';
import './ChangePassword.css';

const getPasswordStrength = (pw, tt) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 2) return { score, label: tt('Weak'), color: '#ef4444' };
  if (score <= 4) return { score, label: tt('Fair'), color: '#f59e0b' };
  if (score === 5) return { score, label: tt('Good'), color: '#22d3ee' };
  return { score, label: tt('Strong'), color: '#10b981' };
};

const ChangePassword = ({ apiBase }) => {
  const { tt } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(tt('All fields are required.'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(tt('New passwords do not match.'));
      return;
    }
    if (newPassword === currentPassword) {
      setError(tt('New password must be different from current password.'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${apiBase}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true, timeout: 15000 }
      );
      setSuccess(response?.data?.message || tt('Password changed successfully.'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || tt('Failed to change password.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-pw-root">
      <section className="change-pw-glass change-pw-hero">
        <div className="change-pw-hero-icon"><FiLock /></div>
        <h1>{tt('Change Password')}</h1>
        <p>{tt("Update your account password. You'll need to enter your current password for verification.")}</p>
      </section>

      <section className="change-pw-glass change-pw-card">
        <form onSubmit={handleSubmit} className="change-pw-form">
          <div className="change-pw-form-group">
            <label htmlFor="cp-current">{tt('Current Password')}</label>
            <input
              id="cp-current"
              type="password"
              className="form-control"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
              aria-invalid={!!error}
              aria-describedby={error ? 'cp-error' : undefined}
            />
          </div>
          <div className="change-pw-form-group">
            <label htmlFor="cp-new">{tt('New Password')}</label>
            <input
              id="cp-new"
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              aria-invalid={!!error}
              aria-describedby={error ? 'cp-error' : undefined}
            />
            <span className="change-pw-hint">
              {tt('Min 10 characters, include uppercase, lowercase, number, and symbol.')}
            </span>
            {newPassword && (() => {
              const strength = getPasswordStrength(newPassword, tt);
              return (
                <div style={{ marginTop: '6px' }}>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} style={{
                        flex: 1, height: '4px', borderRadius: '2px',
                        background: i <= strength.score ? strength.color : 'rgba(100, 116, 139, 0.3)',
                        transition: 'background 0.2s',
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: '12px', color: strength.color }}>{strength.label}</span>
                </div>
              );
            })()}
          </div>
          <div className="change-pw-form-group">
            <label htmlFor="cp-confirm">{tt('Confirm New Password')}</label>
            <input
              id="cp-confirm"
              type="password"
              className="form-control"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              aria-invalid={!!error}
              aria-describedby={error ? 'cp-error' : undefined}
            />
          </div>

          {error && <div className="change-pw-error" id="cp-error" role="alert">{error}</div>}
          {success && <div className="change-pw-success">{success}</div>}

          <button type="submit" className="change-pw-submit" disabled={loading}>
            {loading ? tt('Updating...') : tt('Update Password')}
          </button>
        </form>
      </section>
    </div>
  );
};

export default ChangePassword;
